import { useState, useEffect } from 'react'
import {
  Wallet,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react'
import { BridgeRequest, api, BulkPaymentCheckResponse, BulkPaymentExecuteResponse } from '@/lib/api'
import { cn, shortenAddress } from '@/lib/utils'
import toast from 'react-hot-toast'

interface BulkPaymentModalProps {
  requests: BridgeRequest[]
  onClose: () => void
  onSuccess: () => void
}

type PaymentStatus = 'pending' | 'processing' | 'success' | 'error'

interface RequestStatus {
  id: string
  status: PaymentStatus
  error?: string
  bscTxHash?: string
}

export function BulkPaymentModal({ requests, onClose, onSuccess }: BulkPaymentModalProps) {
  const [isChecking, setIsChecking] = useState(true)
  const [checkResult, setCheckResult] = useState<BulkPaymentCheckResponse | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)
  const [requestStatuses, setRequestStatuses] = useState<RequestStatus[]>([])
  const [executeResult, setExecuteResult] = useState<BulkPaymentExecuteResponse | null>(null)

  // Check if bulk payment can be executed
  useEffect(() => {
    const check = async () => {
      try {
        const result = await api.bulkPaymentCheck(requests.map((r) => r.id))
        setCheckResult(result)
        setRequestStatuses(
          result.requests.map((r) => ({
            id: r.id,
            status: 'pending' as PaymentStatus,
          }))
        )
      } catch (error: any) {
        toast.error(error.message || 'Error verificando pagos')
      } finally {
        setIsChecking(false)
      }
    }
    check()
  }, [requests])

  const handleExecute = async () => {
    if (!checkResult || !checkResult.canExecute) return

    setIsExecuting(true)

    // Mark all as processing
    setRequestStatuses((prev) =>
      prev.map((s) => ({ ...s, status: 'processing' as PaymentStatus }))
    )

    try {
      const result = await api.bulkPaymentExecute(requests.map((r) => r.id))
      setExecuteResult(result)

      // Update individual statuses
      setRequestStatuses(
        result.results.map((r) => ({
          id: r.requestId,
          status: r.success ? 'success' : 'error',
          error: r.error,
          bscTxHash: r.bscTxHash,
        }))
      )

      if (result.completed > 0) {
        toast.success(`${result.completed} pagos completados`)
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} pagos fallaron`)
      }

      onSuccess()
    } catch (error: any) {
      toast.error(error.message || 'Error ejecutando pagos')
      setRequestStatuses((prev) =>
        prev.map((s) => ({ ...s, status: 'error', error: 'Error de ejecución' }))
      )
    } finally {
      setIsExecuting(false)
    }
  }

  const getStatusIcon = (status: PaymentStatus) => {
    switch (status) {
      case 'pending':
        return <span className="w-4 h-4 rounded-full bg-dark-600" />
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-400" />
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />
    }
  }

  const canClose = !isExecuting || executeResult !== null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card p-6 max-w-2xl w-full space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-400" />
            Pagar {requests.length} Solicitudes
          </h3>
          <button
            onClick={onClose}
            disabled={!canClose}
            className="text-dark-400 hover:text-white disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Loading check */}
        {isChecking && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
            <span className="ml-2 text-dark-400">Verificando fondos...</span>
          </div>
        )}

        {/* Check result */}
        {checkResult && !isChecking && (
          <>
            {/* Summary */}
            <div className="bg-dark-800 rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-medium text-dark-300 mb-2">Resumen</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-dark-400">Total USDT:</span>
                  <span className="ml-2 font-mono text-accent-400">
                    {checkResult.totalUsdtFormatted}
                  </span>
                </div>
                <div>
                  <span className="text-dark-400">Gas BSC:</span>
                  <span className="ml-2 font-mono">~{checkResult.totalBnbGas} BNB</span>
                </div>
                <div>
                  <span className="text-dark-400">Gas UNI2:</span>
                  <span className="ml-2 font-mono">~{checkResult.totalUni2Gas}</span>
                </div>
              </div>
            </div>

            {/* Balances */}
            {checkResult.balances && (
              <div className="bg-dark-800 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-dark-300">Verificación de Fondos</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-dark-500 text-xs">Wallet Bridge (BSC)</span>
                    <div className="flex justify-between mt-1">
                      <span className="text-dark-400">USDT:</span>
                      <span
                        className={cn(
                          'font-mono',
                          parseFloat(checkResult.balances.bsc.usdtFormatted || '0') >=
                            parseFloat(checkResult.totalUsdtFormatted)
                            ? 'text-green-400'
                            : 'text-red-400'
                        )}
                      >
                        {checkResult.balances.bsc.usdtFormatted}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-400">BNB:</span>
                      <span
                        className={cn(
                          'font-mono',
                          checkResult.balances.bsc.hasEnoughGas ? 'text-green-400' : 'text-red-400'
                        )}
                      >
                        {checkResult.balances.bsc.bnbFormatted}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-dark-500 text-xs">Wallet Bridge (UNI2)</span>
                    <div className="flex justify-between mt-1">
                      <span className="text-dark-400">UNI2:</span>
                      <span
                        className={cn(
                          'font-mono',
                          checkResult.balances.uni2.hasEnoughGas ? 'text-green-400' : 'text-red-400'
                        )}
                      >
                        {checkResult.balances.uni2.nativeFormatted}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status message */}
            {!checkResult.canExecute && checkResult.reason && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-400">{checkResult.reason}</span>
              </div>
            )}

            {checkResult.canExecute && !executeResult && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-sm text-green-400">Fondos suficientes para ejecutar todos los pagos</span>
              </div>
            )}

            {/* Request list */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-dark-300">Solicitudes a Procesar</h4>
              <div className="bg-dark-800 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-dark-700 text-dark-400">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">#</th>
                      <th className="px-3 py-2 text-left">Destinatario</th>
                      <th className="px-3 py-2 text-right">Cantidad</th>
                      <th className="px-3 py-2 text-center w-20">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-700">
                    {checkResult.requests.map((req, index) => {
                      const status = requestStatuses.find((s) => s.id === req.id)
                      return (
                        <tr key={req.id} className="hover:bg-dark-700/50">
                          <td className="px-3 py-2 text-dark-400">{index + 1}</td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {shortenAddress(req.toAddress, 8)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-accent-400">
                            {req.formattedAmountOut}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-center">
                              {status ? getStatusIcon(status.status) : getStatusIcon('pending')}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Execute result summary */}
            {executeResult && (
              <div className="bg-dark-800 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-medium text-dark-300">Resultado</h4>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-green-400">
                    Completados: {executeResult.completed}
                  </span>
                  {executeResult.failed > 0 && (
                    <span className="text-red-400">
                      Fallidos: {executeResult.failed}
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-2">
          <button
            onClick={onClose}
            disabled={!canClose}
            className="btn-secondary btn-sm"
          >
            {executeResult ? 'Cerrar' : 'Cancelar'}
          </button>
          {checkResult && !executeResult && (
            <button
              onClick={handleExecute}
              disabled={isExecuting || !checkResult.canExecute}
              className="btn-primary btn-sm bg-green-500 hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
            >
              {isExecuting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Wallet className="w-4 h-4" />
                  Ejecutar {requests.length} Pagos
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
